// Booking panel state machine (boards booking/new--existing-customer,
// --walk-in-new-customer, --slot-taken, --staff-pick, created).
//
//   customer ──► service ──► [staff] ──► time ──► confirm ──► created (toast)
//                                          ▲          │
//                                          └─ 409 slot_taken: the slot turns
//                                             red, the coral disables, the
//                                             step stays; picking another slot
//                                             (or day / «الآن») clears it.
//
// The staff step exists only when the server says `staff_pick` (per_staff
// + ≥2 qualified + allow_staff_pick) — absent, not disabled, otherwise.
// Pure reducer: no I/O, no clock reads (the caller passes dates).

import { normalizeIraqiPhone } from '@/lib/digits';
import type { CustomerItem, DeskSource, Slot } from '../data/types';

export type CustomerChoice =
  | { kind: 'existing'; customer: CustomerItem }
  /** `touched` = the person edited the card (until then it follows the search text). */
  | { kind: 'new'; name: string; phone: string; touched: boolean };

export type DayChip = 'today' | 'tomorrow' | 'other';

export type WhenChoice = { kind: 'day'; date: string; chip: DayChip } | { kind: 'now' };

export interface PanelState {
  /** The customer step's search text (fed by the panel field and the global search). */
  query: string;
  customer: CustomerChoice | null;
  serviceId: string | null;
  /** '' = «تلقائي» (the server assigns). */
  staffId: string;
  when: WhenChoice;
  slot: Slot | null;
  /** Slot starts the server refused with 409 slot_taken (drawn struck). */
  taken: string[];
  /** The live 409: its start is outlined red, its detail is the server sentence verbatim. */
  conflict: { start: string; detail: string } | null;
  submitting: boolean;
  /** A non-slot refusal / network failure from the last confirm. */
  error: unknown;
  /** The person pressed the coral with an invalid new-customer card. */
  showErrors: boolean;
  /** `?time=HH:MM` prefill: preselect that slot once the slots arrive. */
  wantTime: string | null;
  /** `?staff_id=` prefill (the empty-slot column of «اليوم» / «التقويم»): kept
   *  across the service pick until the person picks a staff member, or the
   *  service's eligible staff turns out not to include them. */
  wantStaff: string | null;
}

export type PanelAction =
  | { type: 'query'; value: string }
  | { type: 'pickCustomer'; customer: CustomerItem }
  | { type: 'startNew'; name: string; phone?: string }
  | { type: 'editNew'; name?: string; phone?: string }
  | { type: 'changeCustomer' }
  /** The person pressed «تغيير»: drop the customer AND the search text (a phone would re-match at once). */
  | { type: 'clearCustomer' }
  | { type: 'pickService'; serviceId: string }
  | { type: 'changeService' }
  | { type: 'pickStaff'; staffId: string }
  | { type: 'pickDay'; date: string; chip: DayChip }
  | { type: 'pickNow' }
  | { type: 'pickSlot'; slot: Slot }
  | { type: 'wantTimeResolved' }
  /** The prefilled staff member is not eligible for the picked service. */
  | { type: 'wantStaffIneligible' }
  | { type: 'attempt' }
  | { type: 'submit' }
  | { type: 'slotTaken'; start: string; detail: string }
  | { type: 'failed'; error: unknown }
  | { type: 'reset'; state: PanelState };

export interface Prefill {
  query?: string;
  customer?: CustomerItem | null;
  newName?: string;
  serviceId?: string | null;
  staffId?: string | null;
  date?: string | null;
  /** HH:MM Baghdad wall clock. */
  time?: string | null;
  now?: boolean;
}

export function initialState(today: string, prefill: Prefill = {}): PanelState {
  const tomorrow = addDaysPlain(today, 1);
  let when: WhenChoice = { kind: 'day', date: today, chip: 'today' };
  if (prefill.now) when = { kind: 'now' };
  else if (prefill.date) when = { kind: 'day', date: prefill.date, chip: prefill.date === today ? 'today' : prefill.date === tomorrow ? 'tomorrow' : 'other' };
  let customer: CustomerChoice | null = null;
  if (prefill.customer) customer = { kind: 'existing', customer: prefill.customer };
  else if (prefill.newName != null) customer = { kind: 'new', name: prefill.newName, phone: '', touched: prefill.newName !== '' };
  return {
    query: prefill.query ?? '',
    customer,
    serviceId: prefill.serviceId ?? null,
    staffId: prefill.staffId ?? '',
    when,
    slot: null,
    taken: [],
    conflict: null,
    submitting: false,
    error: null,
    showErrors: false,
    wantTime: prefill.time ?? null,
    wantStaff: prefill.staffId || null,
  };
}

function addDaysPlain(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Clears everything downstream of a time change (error, conflict, slot). */
function timeReset(s: PanelState): PanelState {
  return { ...s, slot: null, conflict: null, error: null };
}

export function reducer(s: PanelState, a: PanelAction): PanelState {
  switch (a.type) {
    case 'query':
      return { ...s, query: a.value };
    case 'pickCustomer':
      return { ...s, customer: { kind: 'existing', customer: a.customer }, error: null, showErrors: false };
    case 'startNew':
      return { ...s, customer: { kind: 'new', name: a.name, phone: a.phone ?? '', touched: false }, error: null };
    case 'editNew':
      if (s.customer?.kind !== 'new') return s;
      return { ...s, customer: { ...s.customer, name: a.name ?? s.customer.name, phone: a.phone ?? s.customer.phone, touched: true }, error: null };
    case 'changeCustomer':
      return { ...s, customer: null, showErrors: false, error: null };
    case 'clearCustomer':
      return { ...s, customer: null, query: '', showErrors: false, error: null };
    case 'pickService':
      if (a.serviceId === s.serviceId) return s;
      return { ...timeReset(s), serviceId: a.serviceId, staffId: s.wantStaff ?? '', taken: [] };
    case 'changeService':
      return { ...timeReset(s), serviceId: null, staffId: s.wantStaff ?? '', taken: [] };
    case 'pickStaff':
      if (a.staffId === s.staffId) return s.wantStaff ? { ...s, wantStaff: null } : s;
      return { ...timeReset(s), staffId: a.staffId, taken: [], wantStaff: null };
    case 'wantStaffIneligible':
      if (!s.wantStaff || s.staffId !== s.wantStaff) return s;
      return { ...timeReset(s), staffId: '', taken: [] };
    case 'pickDay':
      return { ...timeReset(s), when: { kind: 'day', date: a.date, chip: a.chip } };
    case 'pickNow':
      return { ...timeReset(s), when: { kind: 'now' } };
    case 'pickSlot':
      if (s.taken.includes(a.slot.start)) return s;
      return { ...s, slot: a.slot, conflict: null, error: null, wantTime: null };
    case 'wantTimeResolved':
      return { ...s, wantTime: null };
    case 'attempt':
      return { ...s, showErrors: true };
    case 'submit':
      return { ...s, submitting: true, error: null, showErrors: true };
    case 'slotTaken':
      return {
        ...s,
        submitting: false,
        slot: null,
        taken: s.taken.includes(a.start) ? s.taken : [...s.taken, a.start],
        conflict: { start: a.start, detail: a.detail },
      };
    case 'failed':
      return { ...s, submitting: false, error: a.error };
    case 'reset':
      return a.state;
    default:
      return s;
  }
}

// ------------------------------------------------------------- derived --

export interface NewCustomerErrors {
  name?: 'required';
  phone?: 'invalid';
}

export function newCustomerErrors(c: CustomerChoice | null): NewCustomerErrors {
  if (c?.kind !== 'new') return {};
  const e: NewCustomerErrors = {};
  if (!c.name.trim()) e.name = 'required';
  if (c.phone.trim() && !normalizeIraqiPhone(c.phone)) e.phone = 'invalid';
  return e;
}

export function customerDone(s: PanelState): boolean {
  if (!s.customer) return false;
  if (s.customer.kind === 'existing') return true;
  const e = newCustomerErrors(s.customer);
  return !e.name && !e.phone;
}

export function timeDone(s: PanelState): boolean {
  return s.when.kind === 'now' || s.slot != null;
}

export type StepKey = 'customer' | 'service' | 'staff' | 'time';

/** The ordered steps (the staff step only when the server allows a pick). */
export function stepsFor(staffPick: boolean): StepKey[] {
  return staffPick ? ['customer', 'service', 'staff', 'time'] : ['customer', 'service', 'time'];
}

export function stepDone(s: PanelState, step: StepKey): boolean {
  switch (step) {
    case 'customer':
      // A new-customer card is still being typed: the step stays «current» (board walk-in).
      return s.customer?.kind === 'existing';
    case 'service':
      return !!s.serviceId;
    case 'staff':
      return !!s.serviceId; // «تلقائي» is a valid choice
    case 'time':
      return timeDone(s);
  }
}

/** The first step that is not done (the dark numbered badge). */
export function currentStep(s: PanelState, steps: StepKey[]): StepKey | null {
  return steps.find((k) => !stepDone(s, k)) ?? null;
}

/** The coral is live: everything chosen, no live conflict, not sending, online. */
export function canConfirm(s: PanelState, online: boolean): boolean {
  return online && !!s.customer && !!s.serviceId && timeDone(s) && !s.conflict && !s.submitting;
}

/** «الآن» = a walk-in at the desk; a chosen slot = a phone booking (board captions). */
export function sourceFor(s: PanelState): DeskSource {
  return s.when.kind === 'now' ? 'walk_in' : 'phone';
}
